import { getCsrfToken } from './csrf';

const API_URL = process.env.REACT_APP_API_URL || '';

export type PushConfig = {
  enabled: boolean;
  publicKey?: string;
};

let cachedConfig: PushConfig | null = null;

export async function getPushConfig(): Promise<PushConfig> {
  if (cachedConfig) return cachedConfig;
  if (!API_URL) {
    return { enabled: false };
  }
  try {
    const res = await fetch(`${API_URL}/api/meta`);
    if (!res.ok) return { enabled: false };
    const data = await res.json();
    const push = data?.push;
    cachedConfig = {
      enabled: Boolean(push?.enabled && push?.publicKey),
      publicKey: typeof push?.publicKey === 'string' ? push.publicKey : undefined,
    };
    return cachedConfig;
  } catch {
    return { enabled: false };
  }
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) {
    output[i] = raw.charCodeAt(i);
  }
  return output;
}

async function authHeaders(accessToken: string): Promise<Record<string, string>> {
  const csrf = await getCsrfToken();
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
    'X-CSRF-Token': csrf,
  };
}

const SW_TIMEOUT_MS = 12_000;

export async function waitForServiceWorkerRegistration(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null;

  const existing = await navigator.serviceWorker.getRegistration();
  if (existing?.active) return existing;

  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve(null), SW_TIMEOUT_MS);
    navigator.serviceWorker.ready
      .then((reg) => {
        clearTimeout(timer);
        resolve(reg);
      })
      .catch(() => {
        clearTimeout(timer);
        resolve(null);
      });
  });
}

export async function subscribeDevicePush(
  accessToken: string,
  registration: ServiceWorkerRegistration
): Promise<boolean> {
  const config = await getPushConfig();
  if (!config.enabled || !config.publicKey) return false;

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return false;

  let sub = await registration.pushManager.getSubscription();
  if (!sub) {
    sub = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(config.publicKey),
    });
  }

  const json = sub.toJSON();
  if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) {
    return false;
  }

  const headers = await authHeaders(accessToken);
  const res = await fetch(`${API_URL}/api/push/subscribe`, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: JSON.stringify({
      endpoint: json.endpoint,
      keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
      userAgent: navigator.userAgent,
    }),
  });

  return res.ok;
}

export async function unsubscribeDevicePush(
  accessToken: string,
  registration?: ServiceWorkerRegistration | null
): Promise<boolean> {
  const reg = registration ?? (await waitForServiceWorkerRegistration());
  const sub = reg ? await reg.pushManager.getSubscription() : null;
  const endpoint = sub?.endpoint;

  if (sub) {
    await sub.unsubscribe();
  }

  const headers = await authHeaders(accessToken);
  const res = await fetch(`${API_URL}/api/push/subscribe`, {
    method: 'DELETE',
    credentials: 'include',
    headers,
    body: JSON.stringify({ endpoint }),
  });

  return res.ok;
}

export async function hasActivePushSubscription(): Promise<boolean> {
  const reg = await waitForServiceWorkerRegistration();
  if (!reg) return false;
  const sub = await reg.pushManager.getSubscription();
  return Boolean(sub);
}
